import { BRAND_WORDMARK } from './brand';
import { BRANDING, brandingCopyright, brandingLocalita } from './branding';
import { BRAND_PRIMARY, BRAND_PRIMARY_DARK } from './brandColors';

/**
 * Template HTML delle email inviate dall'app (offerte, lead).
 *
 * Tutto cio' che identifica l'azienda — nome, sede, P.IVA, contatti — arriva
 * da `branding.ts`, e i colori da `brandColors.ts`: qui non deve restare
 * niente scritto a mano.
 */

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function linkify(escaped: string, linkColor: string): string {
  return escaped.replace(
    /(https?:\/\/[^\s<]+)/g,
    `<a href="$1" style="color: ${linkColor}; text-decoration: underline;" target="_blank" rel="noopener noreferrer">$1</a>`
  );
}

function formatEmailBody(emailBody: string, linkColor: string): string {
  return emailBody
    .split('\n')
    .map((line) => {
      const trimmed = line.trim();
      if (trimmed === '') {
        return '<p style="margin: 0 0 12px 0;"></p>';
      }
      const content = linkify(escapeHtml(trimmed), linkColor);
      return `<p style="margin: 0 0 12px 0; color: #374151; line-height: 1.7; font-size: 15px;">${content}</p>`;
    })
    .join('');
}

/**
 * Dominio pubblico su cui gli asset sono davvero raggiungibili.
 * Serve in sviluppo: un URL localhost dentro un'email e' un'immagine rotta
 * per chi la riceve.
 */
const PUBLIC_APP_URL = (import.meta.env?.VITE_PUBLIC_APP_URL as string | undefined)
  ?.replace(/\/+$/, '') || '';

function absolutePublicUrl(path: string, localhostFallback?: string): string {
  const fallback = localhostFallback
    ?? (PUBLIC_APP_URL ? `${PUBLIC_APP_URL}${path}` : undefined);
  if (typeof window !== 'undefined') {
    const origin = window.location.origin;
    if (origin.includes('localhost') || origin.includes('127.0.0.1')) {
      return fallback ?? `${origin}${path}`;
    }
    return `${origin}${path}`;
  }
  return fallback ?? path;
}

/** Genera il template HTML dell'email nei colori di marca. */
export function generateEmailHTML(
  emailBody: string,
  _customerName?: string,
  _priceListName?: string
): string {
  const formattedBody = formatEmailBody(emailBody, BRAND_PRIMARY);

  const logoUrl = absolutePublicUrl(BRAND_WORDMARK);

  return `<!DOCTYPE html>
<html lang="it">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${BRANDING.nomeBreve}</title>
</head>
<body style="margin: 0; padding: 0; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f3f4f6;">
  <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color: #f3f4f6; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="600" style="background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1); max-width: 600px;">
          <!-- Header con logo e barra colorata -->
          <tr>
            <td style="background: linear-gradient(135deg, ${BRAND_PRIMARY} 0%, ${BRAND_PRIMARY_DARK} 100%); padding: 40px 30px; text-align: center;">
              <img src="${logoUrl}" alt="${BRANDING.nomeBreve}" style="max-width: 220px; height: auto; display: block; margin: 0 auto;" />
            </td>
          </tr>
          
          <!-- Contenuto principale -->
          <tr>
            <td style="padding: 50px 40px;">
              <div style="color: #374151; line-height: 1.7;">
                ${formattedBody}
              </div>
            </td>
          </tr>
          
          <!-- Separatore decorativo -->
          <tr>
            <td>
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                <tr>
                  <td style="padding: 0 40px;">
                    <div style="height: 3px; background: linear-gradient(90deg, transparent, ${BRAND_PRIMARY} 50%, transparent); border-radius: 2px;"></div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          
          <!-- Footer -->
          <tr>
            <td style="padding: 35px 40px; background-color: #f9fafb;">
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                <tr>
                  <td align="center" style="padding: 0;">
                    <p style="margin: 0 0 10px 0; color: #6b7280; font-size: 14px; line-height: 1.6;">
                      <strong style="color: ${BRAND_PRIMARY}; font-size: 15px;">${BRANDING.ragioneSociale}</strong><br/>
                      ${[BRANDING.indirizzo, brandingLocalita()].filter(Boolean).join(' - ')}<br/>
                      ${BRANDING.partitaIva ? `P.IVA: ${BRANDING.partitaIva}<br/>` : ''}
                      ${[
                        BRANDING.email && `<a href="mailto:${BRANDING.email}" style="color: ${BRAND_PRIMARY}; text-decoration: none;">${BRANDING.email}</a>`,
                        BRANDING.sito && `<a href="https://${BRANDING.sito.replace(/^https?:\/\//, '')}" style="color: ${BRAND_PRIMARY}; text-decoration: none;">${BRANDING.sito}</a>`,
                        BRANDING.whatsapp && `WhatsApp: <a href="https://wa.me/${BRANDING.whatsapp}" style="color: ${BRAND_PRIMARY}; text-decoration: none;">${BRANDING.telefono || BRANDING.whatsapp}</a>`
                      ].filter(Boolean).join(' | ')}
                    </p>
                    <p style="margin: 20px 0 0 0; color: #9ca3af; font-size: 12px; line-height: 1.5;">
                      ${brandingCopyright()}
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

/**
 * L'app inviava le email dei lead scegliendo fra piu' template; l'unico
 * alternativo era quello del brand POSA, un altro progetto di Abruzzo.AI,
 * che era anche quello proposto per primo. Rimosso: resta un solo template,
 * quello dell'azienda intestataria.
 */
export function generateLeadEmailHTML(emailBody: string): string {
  return generateEmailHTML(emailBody);
}
